import { EditorType } from './types';

/** Popover placement for the wizard header more-actions menu. */

/**
 * Accessibility wizard UX that varies by shell integration, not by editor “brand”.
 * All flags are decided in {@link resolveEditorCapabilities} so call sites stay host-agnostic.
 */

/**
 * Derived feature flags for accessibility UI and rule behavior.
 * Resolve once per session (wizard) or job (analyzer) from EditorType.
 * Keep EditorType on payloads, BI, and site metadata.
 */

const wizardPresentationClassic = {
  softIssueListSurfaces: false,
  contrastPopoverDarkSkin: false,
  welcomeDomOrderOptIn: false,
  helpArticlesUseOpenArticleKey: false,
  headerMenuPlacement: 'left-start',
  enlargedPreviewSize: {
    width: 226,
    maxHeight: 150
  },
  imageGridGap: 'SP1',
  ruleIconButtonPriority: 'secondary'
};
const wizardPresentationStudio = {
  softIssueListSurfaces: true,
  contrastPopoverDarkSkin: true,
  welcomeDomOrderOptIn: true,
  helpArticlesUseOpenArticleKey: true,
  headerMenuPlacement: 'bottom-start',
  enlargedPreviewSize: {
    width: 226,
    maxHeight: 150
  },
  imageGridGap: 'SP2',
  ruleIconButtonPriority: 'secondary'
};
const wizardPresentationOd = {
  softIssueListSurfaces: false,
  contrastPopoverDarkSkin: false,
  welcomeDomOrderOptIn: false,
  helpArticlesUseOpenArticleKey: false,
  headerMenuPlacement: 'bottom',
  enlargedPreviewSize: {
    width: 362,
    maxHeight: 200
  },
  imageGridGap: 'SP2',
  ruleIconButtonPriority: 'primary'
};
const wizardPresentationStudioTwo = {
  ...wizardPresentationOd,
  softIssueListSurfaces: true,
  contrastPopoverDarkSkin: true,
  welcomeDomOrderOptIn: true,
  helpArticlesUseOpenArticleKey: true,
  headerMenuPlacement: 'bottom-start',
  ruleIconButtonPriority: 'secondary',
  ruleIconButtonSkin: 'inverted'
};
const engineDefaultsStudio = {
  usesStaticClassicBreakpoints: false,
  autoDomOrderFromStudioFlag: true,
  axeSeparateMobileContext: false,
  expandsBuilderMediaComponents: false,
  usesSeparateMobileComponentsMap: false,
  axeOdThunderboltExperiments: false,
  enforcesProductPageLayoutRule: false
};
const engineDefaultsClassic = {
  usesStaticClassicBreakpoints: true,
  autoDomOrderFromStudioFlag: false,
  axeSeparateMobileContext: true,
  expandsBuilderMediaComponents: false,
  usesSeparateMobileComponentsMap: true,
  axeOdThunderboltExperiments: false,
  enforcesProductPageLayoutRule: true
};
const engineDefaultsOd = {
  usesStaticClassicBreakpoints: false,
  autoDomOrderFromStudioFlag: false,
  axeSeparateMobileContext: false,
  expandsBuilderMediaComponents: true,
  usesSeparateMobileComponentsMap: false,
  axeOdThunderboltExperiments: true,
  enforcesProductPageLayoutRule: false
};
const defaultCapabilities = {
  compactPanelChrome: false,
  usesClassicModalChrome: false,
  usesClassBasedColorContrastUpdates: false,
  usesClassicCompRefWithBreakpoint: false,
  supportsNonClassicDomOrderFix: true,
  supportsOpenArticleById: false,
  odDomOrderPolicy: false,
  wizardPresentation: wizardPresentationStudio,
  requiresExplicitTpaIds: false,
  usesClassicMenuHierarchy: false,
  ...engineDefaultsStudio
};
const classic = {
  ...defaultCapabilities,
  usesClassicModalChrome: true,
  usesClassicCompRefWithBreakpoint: true,
  supportsNonClassicDomOrderFix: false,
  wizardPresentation: wizardPresentationClassic,
  requiresExplicitTpaIds: true,
  usesClassicMenuHierarchy: true,
  ...engineDefaultsClassic
};
const odEditor = {
  ...defaultCapabilities,
  compactPanelChrome: true,
  usesClassBasedColorContrastUpdates: true,
  supportsOpenArticleById: true,
  odDomOrderPolicy: true,
  wizardPresentation: wizardPresentationOd,
  ...engineDefaultsOd
};
const studioTwoEditor = {
  ...odEditor,
  wizardPresentation: wizardPresentationStudioTwo
};

/**
 * Pure lookup: same {@link EditorType} always yields the same capabilities.
 * Add new editors by extending {@link EditorType} and mapping here (multiple types may share a row).
 */
export function resolveEditorCapabilities(editorType) {
  switch (editorType) {
    case EditorType.Classic:
      return classic;
    case EditorType.Studio:
      return defaultCapabilities;
    case EditorType.StudioTwo:
      return studioTwoEditor;
    case EditorType.OdEditor:
      return odEditor;
    default:
      {
        throw new Error(`Unsupported editor type for capabilities resolver: ${String(editorType)}`);
      }
  }
}
//# sourceMappingURL=editor-capabilities.js.map